import {
  Button,
  ButtonGroup,
  Fieldset,
  Form,
  ListBoxItem,
  Select,
  TextInput,
} from "@datapay-by-datacom/design-system";
import { useNavigate } from "react-router";
import { useTriggerRunForm } from "../../hooks/useTriggerRunForm";
import { AdvancedOptionsFieldset } from "../AdvancedOptionsFieldset/AdvancedOptionsFieldset";
import styles from "./TriggerRunForm.module.css";

export function TriggerRunForm() {
  const navigate = useNavigate();
  const {
    field,
    input,
    orgs,
    selectedOrgKey,
    setSelectedOrgKey,
    selectedAgentId,
    serviceAccounts,
    serviceAccountId,
    selectedServiceAccountId,
    setSelectedServiceAccountId,
    agentDetail,
    isAgentLoading,
    directoryPrefixes,
    isDocumentsError,
    documentsError,
    validationError,
    isAgentConfigured,
    configuredAgentMissing,
    isAgentConfigLoading,
    handleSubmit,
    mutation,
  } = useTriggerRunForm();

  const selectedServiceAccount = serviceAccounts.find(
    (sa) => sa.id === serviceAccountId,
  );

  if (isAgentConfigLoading) {
    return <p className={styles.warningText}>Loading configuration…</p>;
  }

  if (!isAgentConfigured) {
    return (
      <p className={styles.warningText}>
        Shadow run agent is not configured for this environment.
      </p>
    );
  }

  if (configuredAgentMissing) {
    return (
      <p className={styles.warningText}>
        Configured shadow run agent was not found. Check SHADOW_RUN_AGENT_ID
        for this environment.
      </p>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Fieldset>
        {isAgentLoading ? (
          <p className={styles.infoPanel}>Loading agent…</p>
        ) : agentDetail ? (
          <div className={styles.infoPanel} data-testid="agent-info-panel">
            <p className={styles.infoPanelTitle}>Agent</p>
            <dl className={styles.definitionList}>
              <dt>Name</dt>
              <dd>{agentDetail.blueprint.name}</dd>
              <dt>Description</dt>
              <dd>{agentDetail.blueprint.description || "—"}</dd>
              <dt>Version</dt>
              <dd>{agentDetail.blueprint.version || "—"}</dd>
            </dl>
          </div>
        ) : null}

        {serviceAccounts.length > 1 ? (
          <Select
            aria-label="Service Account"
            label="Service Account"
            selectedKey={selectedServiceAccountId || null}
            onSelectionChange={(key) =>
              setSelectedServiceAccountId(key as string)
            }
          >
            {serviceAccounts.map((sa) => (
              <ListBoxItem key={sa.id} id={sa.id}>
                {sa.description || sa.id}
              </ListBoxItem>
            ))}
          </Select>
        ) : selectedServiceAccount ? (
          <div
            className={styles.infoPanel}
            data-testid="service-account-panel"
          >
            <p className={styles.infoPanelTitle}>Service Account</p>
            <dl className={styles.definitionList}>
              <dt>ID</dt>
              <dd>{selectedServiceAccount.id}</dd>
              <dt>Description</dt>
              <dd>{selectedServiceAccount.description || "—"}</dd>
            </dl>
          </div>
        ) : (
          <div className={styles.warningText} data-testid="no-service-account-warning">
            <p>
              No service account configured for the selected agent. A
              provisioner must create one before runs can be triggered.
            </p>
          </div>
        )}

        <Select
          aria-label="Organisation"
          label="Organisation"
          selectedKey={selectedOrgKey}
          onSelectionChange={(key) => setSelectedOrgKey(key as string)}
          isDisabled={!orgs.length}
        >
          {orgs.map((org) => (
            <ListBoxItem
              key={`${org.category}:${org.id}`}
              id={`${org.category}:${org.id}`}
            >
              {org.name}
            </ListBoxItem>
          ))}
        </Select>

        {directoryPrefixes.length > 0 ? (
          <Select
            aria-label="Directory Path"
            label="Directory Path"
            description="Select the payroll data directory to process"
            selectedKey={field.value.directoryPath || null}
            onSelectionChange={(key) =>
              field.onChange({ ...field.value, directoryPath: key as string })
            }
          >
            {directoryPrefixes.map((prefix) => (
              <ListBoxItem key={prefix} id={prefix}>
                {prefix}
              </ListBoxItem>
            ))}
          </Select>
        ) : (
          <TextInput
            label="Directory Path"
            description="Path to the payroll data directory to process"
            isRequired
            {...input("directoryPath")}
          />
        )}

        {isDocumentsError && (
          <div className={styles.error} data-testid="documents-error">
            <p>
              Failed to load directories:{" "}
              {documentsError instanceof Error
                ? documentsError.message
                : "Could not fetch organisation documents"}
            </p>
          </div>
        )}

        <Select
          aria-label="Scale"
          label="Scale"
          selectedKey={field.value.scale}
          onSelectionChange={(key) =>
            field.onChange({
              ...field.value,
              scale: key as "standard" | "enterprise",
            })
          }
        >
          <ListBoxItem id="standard">Standard</ListBoxItem>
          <ListBoxItem id="enterprise">Enterprise</ListBoxItem>
        </Select>

        <AdvancedOptionsFieldset
          field={field}
          input={input}
          isEnterprise={field.value.scale === "enterprise"}
        />

        {validationError && (
          <div className={styles.error} data-testid="validation-error">
            <p>{validationError}</p>
          </div>
        )}

        {mutation.isError && (
          <div className={styles.error}>
            <p>
              Error: {mutation.error?.message || "Failed to trigger shadow run"}
            </p>
          </div>
        )}

        <ButtonGroup align="right">
          <Button
            variant="secondary"
            onPress={() => navigate("/shadow-runs")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={
              mutation.isPending || !selectedAgentId || !serviceAccountId
            }
          >
            {mutation.isPending ? "Triggering..." : "Trigger Run"}
          </Button>
        </ButtonGroup>
      </Fieldset>
    </Form>
  );
}
